import type { PackageResponse } from '../../types/packages';
import { formatInstantDate } from '../offers/offerUtils';
import { RUNNING_STATUSES } from './packageUtils';
import { reasonText, rejectReasonText, type PackagesT } from './packagesI18n';

export interface PackageNoticesProps {
  t: PackagesT;
  lang: 'en' | 'ar';
  pkg: PackageResponse | null;
}

/** Status notices above the editor: rejected/suspended reasons, pending, running (edits apply now), ended, unavailable. */
export default function PackageNotices({ t, lang, pkg }: PackageNoticesProps) {
  if (!pkg) return null;
  const { status } = pkg;
  return (
    <>
      {pkg.lastEditedBy === 'ADMIN' && pkg.lastEditedAt && (
        <p className="offer-admin-edited">{t('packages.adminEdited', { date: formatInstantDate(pkg.lastEditedAt, lang) })}</p>
      )}
      {status === 'REJECTED' && (
        <div className="offer-notice offer-notice--danger" data-testid="pkg-rejection-banner">
          <strong>{t('packages.notice.rejected')}</strong>
          {pkg.rejectionReasonCode && <div className="offer-notice__reason">{rejectReasonText(t, pkg.rejectionReasonCode)}</div>}
          {pkg.rejectionReason && <div className="offer-notice__text">{pkg.rejectionReason}</div>}
          <div className="offer-notice__hint">{t('packages.notice.rejectedHint')}</div>
        </div>
      )}
      {status === 'SUSPENDED' && (
        <div className="offer-notice offer-notice--danger">
          <strong>{t('packages.notice.suspended')}</strong>
          {pkg.suspendReason && <div className="offer-notice__text">{pkg.suspendReason}</div>}
          <div className="offer-notice__hint">{t('packages.notice.suspendedHint')}</div>
        </div>
      )}
      {status === 'PENDING_APPROVAL' && <div className="offer-notice offer-notice--info">{t('packages.notice.pending')}</div>}
      {RUNNING_STATUSES.includes(status) && (
        <div className="offer-notice offer-notice--info">
          {t('packages.notice.running', { status: t(`packages.status.${status}`) })}
        </div>
      )}
      {status === 'ENDED' && <div className="offer-notice">{t('packages.notice.ended')}</div>}
      {pkg.unavailableReason && status !== 'ENDED' && (
        <div className="offer-notice offer-notice--warn" data-testid="pkg-unavailable-notice">
          {t('packages.notice.unavailable', { reason: reasonText(t, pkg.unavailableReason) })}
        </div>
      )}
    </>
  );
}
